/**
 * /about — a PAGE. AppLayoutRoute supplies the chrome.
 *
 * It used to carry the template's own instruction — "Replace this with the
 * specific problem you solve and who has it" — as published copy, at 124 words,
 * pre-rendered and in the sitemap. An about page is one of the few a person
 * reads before deciding whether to trust you with production systems, so it now
 * says who operates this, why it exists and how it is run.
 *
 * Nothing here claims anything unverifiable: no customer counts, no years in
 * business, no team photographs. What it asserts about the platform is true of
 * the code that runs it.
 */
import { Link } from 'react-router-dom';
import { Button } from '@bloomneo/uikit';
import { ArrowRight, Compass, Layers, ShieldCheck, Wrench, Clock, Building2 } from 'lucide-react';
import { SEO } from '@/shared/SEO';
import { brand } from '@/shared/brand';

const PRINCIPLES = [
  {
    icon: Compass,
    title: 'Operated, not resold',
    body: 'We run the servers rather than passing you to a provider and charging a margin. When something breaks at 2am, the person who set it up is the person who fixes it.',
  },
  {
    icon: Layers,
    title: 'Boring where it counts',
    body: 'Ubuntu, nginx, Postgres, pm2, Let’s Encrypt. Familiar parts, assembled the same way every time, because novelty in infrastructure is a cost paid at the worst moment.',
  },
  {
    icon: ShieldCheck,
    title: 'Your data stays yours',
    body: 'Domains are registered to you, backups are handed over on request, and leaving takes 30 days’ notice and nothing else. Stated here so we can be held to it.',
  },
];

const FACTS = [
  { icon: Building2, label: 'Operated by', value: 'Krishna Teja GS, Hyderabad' },
  { icon: Wrench, label: 'Built on', value: 'The Bloom framework' },
  { icon: Clock, label: 'Watched', value: 'Continuously, with nightly offsite backups' },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Bloomneo — Managed Server Infrastructure"
        description="Bloomneo is managed server infrastructure operated from Hyderabad: servers, databases, certificates, backups and monitoring on one plan."
      />

      <div className="py-8">
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-14">
          <div className="min-w-0 max-w-[68ch]">
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              About {brand.name}
            </h1>

            <div className="mt-6 space-y-4 text-lg leading-relaxed text-muted-foreground">
              <p>
                {brand.name} is managed server infrastructure, run from Hyderabad. It exists
                because of a pattern that kept repeating across years of building software for
                other people: we would build something, hand it over, and be called back months
                later — not
                because the software had failed, but because a certificate had expired, a disk had
                filled, or a backup nobody had checked turned out not to exist.
              </p>
              <p>
                That work never fits a project. It is continuous, unglamorous, and it only becomes
                visible on the day it has been neglected. So we run it as a service instead:
                servers, databases, domains, certificates, backups, storage, email and monitoring,
                on one plan, for businesses that would rather not hire someone to remember all of
                it.
              </p>
            </div>
          </div>

          {/*
            The facts, pulled out of the prose. A reader deciding whether to
            hand over production wants to know who they are dealing with before
            they finish the second paragraph.
          */}
          <aside className="mt-10 lg:mt-0">
            <dl className="space-y-5 rounded-xl border border-border bg-muted/30 p-5">
              {FACTS.map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex gap-3">
                  <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {label}
                    </dt>
                    <dd className="mt-0.5 text-sm leading-snug">{value}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </aside>
        </div>

        <h2 className="mt-14 text-xl font-semibold tracking-tight">How we work</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {PRINCIPLES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-xl border border-border bg-card p-5">
              <span className="grid size-9 place-items-center rounded-lg bg-primary/10 text-primary">
                <Icon className="size-4" />
              </span>
              <h3 className="mt-3.5 text-sm font-semibold">{title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-border bg-muted/40 p-6 sm:p-8">
          <h2 className="text-xl font-semibold tracking-tight">What being managed by us means</h2>
          <p className="mt-3 max-w-[68ch] leading-relaxed text-muted-foreground">
            Every database is dumped nightly to offsite storage and kept for 30 days. Certificates
            renew automatically and their expiry is tracked per domain. Uptime is checked from
            outside your servers, so a host that has stopped answering cannot fail to report it.
            Operating systems are patched on a schedule, access is key-only behind a firewall, and
            a deploy keeps the previous release on disk so a bad one is a rollback rather than an
            outage.
          </p>
          <p className="mt-3 max-w-[68ch] leading-relaxed text-muted-foreground">
            None of that is remarkable on its own. It is remarkable how often it is nobody's job.
          </p>
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-border pt-8">
          <Button asChild>
            <Link to="/contact">Talk to us <ArrowRight className="size-4" /></Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/usecases">See what we are called for</Link>
          </Button>
        </div>
      </div>
    </>
  );
}
